import { Box, Button, IconButton, Modal, Typography } from '@mui/material';
import styles from './AddEmployeeViaOfficeModal.module.css';
import CloseIcon from '@mui/icons-material/Close';
import NewEmployeesModal from '../NewEmployeesModal';
import { useState, useRef, ChangeEvent } from 'react';
import { ExcelToJSON } from './readExcel';
import { EmployeesService, RegisterEmployee, RegisterEmployees_In } from 'src/pehra-ts-client';
import useNotification from 'src/hooks/useNotification';
import { LoadingButton } from '@mui/lab';
import { useMutation, useQueryClient } from '@tanstack/react-query';
export interface IAddEmployeeViaOfficeModal {
  openModal: boolean;
  handleCloseModal: () => void;
  officeId: string;
}

const AddEmployeeViaOfficeModal: React.FC<IAddEmployeeViaOfficeModal> = ({ openModal, handleCloseModal, officeId }) => {
  const [openNewEmployeeModal, setOpenNewEmployeeModal] = useState<boolean>(false);

  const [sendNotification] = useNotification();
  const queryClient = useQueryClient();

  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleButtonClick() {
    fileInputRef.current?.click();
  }

  const handleCloseNewEmployeeModal = () => {
    setOpenNewEmployeeModal(false);
  };

  const registerEmployee = async (employeeInfo: any) => {
    const employeesList: RegisterEmployee[] = [];

    employeeInfo.forEach((employee: any) => {
      const employeeObj: RegisterEmployee = {
        badge_number: employee['badge_number'] ? parseInt(employee['badge_number']) : null,
        rank: employee['rank'] ? employee['rank'] : null,
        pno_number: parseInt(employee['pno_number']),
        ehrms_code: employee['ehrms_code'] ? employee['ehrms_code'].toString() : null,
        name: employee['name'] ? employee['name'] : '',
        father_name: employee['father_name'] ? employee['father_name'] : null,
        date_of_birth: employee['date_of_birth'] ? employee['date_of_birth'] : null,
        date_of_appointment: employee['date_of_appointment'] ? employee['date_of_appointment'] : null,
        type_of_appointment: employee['type_of_appointment'] ? employee['type_of_appointment'] : null,
        gender: employee['gender'] ? employee['gender'] : null,
        caste: employee['caste'] ? employee['caste'] : null,
        category: employee['category'] ? employee['category'] : null,
        education: employee['education'] ? employee['education'] : null,
        special_qualifications: employee['special_qualifications'] ? employee['special_qualifications'].split(',') : null,
        home_district: employee['home_district'] ? employee['home_district'] : null,
        current_position: employee['current_position'] ? employee['current_position'] : null,
        date_of_current_position: employee['date_of_current_position'] ? employee['date_of_current_position'] : null,
        date_of_agra_posting: employee['date_of_agra_posting'] ? employee['date_of_agra_posting'] : null,
        current_posting_role: employee['current_posting_role'] ? employee['current_posting_role'] : null,
        date_of_posting_to_zone: employee['date_of_posting_to_zone'] ? employee['date_of_posting_to_zone'] : null,
        previous_postings: employee['previous_postings'] ? employee['previous_postings'] : null,
        punishments: employee['punishments'] ? employee['punishments'] : null,
        other_details: employee['other_details'] ? employee['other_details'] : null
      };
      employeesList.push(employeeObj);
    });

    const registerEmployeeBody: RegisterEmployees_In = {
      employees: employeesList,
      office_id: officeId
    };
    await EmployeesService.registerEmployees(registerEmployeeBody);
  };

  const registerEmployeeMutation = useMutation({
    mutationFn: registerEmployee,
    onSuccess: () => {
      sendNotification({
        msg: 'Employees added successfully',
        type: 'success'
      });
      queryClient.invalidateQueries({
        queryKey: ['employeesList', officeId]
      });
      handleCloseModal();
    },
    onError: (error: any) => {
      sendNotification({
        msg: 'Fail' + error,
        type: 'error'
      });
    }
  });

  function handleSelectFile(event: ChangeEvent<HTMLInputElement>) {
    const target = event.target;
    try {
      if (target.files && target.files[0]) {
        const file = target.files[0];
        const reader = new FileReader();
        reader.onload = async (e) => {
          const data = e.target?.result;
          if (data) {
            try {
              const jsonData = ExcelToJSON(data);
              registerEmployeeMutation.mutate(jsonData);
            } catch (error: any) {
              alert(error);
            }
          }
        };
        reader.readAsArrayBuffer(file);
      }
    } catch (error: any) {
      console.error(error);
      sendNotification({
        msg: 'Fail' + error,
        type: 'error'
      });
    }
  }

  const handleDownloadSampleExcel = () => {
    const link = document.createElement('a');
    link.href = `${process.env.PUBLIC_URL}/sample_employee_excel.xlsx`;
    link.download = 'sample_employee_excel.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal open={openModal} onClose={handleCloseModal}>
      <Box className={styles.modalContainer}>
        <IconButton onClick={handleCloseModal} style={{ position: 'absolute', right: '10px', top: '10px' }}>
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            marginTop: '50px',
            marginBottom: '50px',
            gap: '20px'
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setOpenNewEmployeeModal(true);
            }}
          >
            Add Employee Manually
          </Button>
          <input type="file" ref={fileInputRef} accept="csv" style={{ display: 'none' }} onChange={handleSelectFile} />

          <LoadingButton
            variant="contained"
            color="primary"
            onClick={handleButtonClick}
            loading={registerEmployeeMutation.isPending}
            loadingIndicator="Loading..."
          >
            Bulk Add using Excel
          </LoadingButton>

          <Button variant="text" color="primary" onClick={handleDownloadSampleExcel}>
            Download sample excel file
          </Button>
        </Box>
        <NewEmployeesModal openModal={openNewEmployeeModal} handleCloseModal={handleCloseNewEmployeeModal} officeId={officeId} />
      </Box>
    </Modal>
  );
};

export default AddEmployeeViaOfficeModal;
